import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { testimonials } from '../../../mocks/testimonials';

export default function ReviewsSection() {
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) ref.current?.classList.add('visible'); }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setActive((p) => (p + 1) % testimonials.length), 6000);
    return () => clearInterval(t);
  }, []);

  const current = testimonials[active];

  return (
    <section className="bg-dark py-28 overflow-hidden">
      <div ref={ref} className="fade-up max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Customer Stories</span>
            </div>
            <h2 className="font-serif text-5xl font-bold text-white leading-tight mb-10">
              Homes We've<br />
              <em className="text-gold not-italic">Transformed</em>
            </h2>

            {/* Featured Review */}
            <div className="relative">
              <div className="font-serif text-[80px] leading-none text-white/6 absolute -top-4 -left-2 select-none">"</div>
              <div className="relative z-10">
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-gold text-sm" />
                  ))}
                </div>
                <p className="text-white/80 text-lg leading-relaxed mb-8 font-light italic">
                  "{current.text}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center font-serif font-bold text-gold text-lg flex-shrink-0">
                    {current.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-white font-semibold">{current.name}</div>
                    <div className="text-white/40 text-xs">{current.project}</div>
                    <div className="text-white/30 text-xs">{current.location} · {current.date}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Dots */}
            <div className="flex gap-2 mt-10">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`transition-all cursor-pointer ${i === active ? 'w-8 h-1.5 bg-gold rounded-full' : 'w-1.5 h-1.5 bg-white/20 rounded-full hover:bg-white/40'}`}
                />
              ))}
            </div>
          </div>

          {/* Right - Mini Reviews */}
          <div className="flex flex-col gap-4">
            {testimonials.filter((_, i) => i !== active).slice(0, 3).map((t) => (
              <button
                key={t.id}
                onClick={() => setActive(testimonials.findIndex((x) => x.id === t.id))}
                className="text-left p-6 rounded-xl bg-white/4 border border-white/8 hover:bg-white/7 hover:border-gold/30 transition-all cursor-pointer group"
              >
                <div className="flex gap-1 mb-2">
                  {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-gold text-xs" />)}
                </div>
                <p className="text-white/60 text-sm line-clamp-2 group-hover:text-white/80 transition-colors">"{t.text}"</p>
                <div className="mt-3 text-xs text-white/30 font-semibold">{t.name} — {t.location}</div>
              </button>
            ))}

            <Link
              to="/reviews"
              className="mt-2 flex items-center justify-center gap-2 border border-gold/40 text-gold px-6 py-4 rounded-xl text-sm font-semibold uppercase tracking-wider hover:bg-gold hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              Read All Reviews
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
